import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  TrendingUp,
  TrendingDown,
  AccountBalance,
} from '@mui/icons-material';
import { portfolioService, StockTransactionsAcrossPortfoliosResponse, PortfolioTransactions } from '../../services/portfolioService';

interface StockTransactionHistoryProps {
  stockSymbol: string;
}

const StockTransactionHistory: React.FC<StockTransactionHistoryProps> = ({ stockSymbol }) => {
  const [data, setData] = useState<StockTransactionsAcrossPortfoliosResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    loadTransactionHistory();
  }, [stockSymbol]);

  const loadTransactionHistory = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await portfolioService.getStockTransactionsAcrossPortfolios(stockSymbol);
      setData(response);
    } catch (err: any) {
      console.error('Error loading transaction history:', err);
      setError('Failed to load transaction history');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getTransactionTypeColor = (type: string) => {
    switch (type) {
      case 'BUY':
      case 'OPEN_POSITION':
        return 'success';
      case 'SELL':
        return 'error';
      case 'BONUS':
        return 'info';
      default:
        return 'default';
    }
  };

  const getTransactionTypeIcon = (type: string) => {
    switch (type) {
      case 'BUY':
      case 'OPEN_POSITION':
        return <TrendingUp fontSize="small" />;
      case 'SELL':
        return <TrendingDown fontSize="small" />;
      case 'BONUS':
        return <AccountBalance fontSize="small" />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={4}>
        <CircularProgress />
        <Typography variant="body2" sx={{ ml: 2 }}>
          Loading transaction history...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!data || data.total_transactions === 0) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        No transactions found for {stockSymbol}
      </Alert>
    );
  }

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6" gutterBottom>
        Transaction History - {data.stock.name} ({data.stock.symbol})
      </Typography>

      {/* Overall Summary */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom>
            Overall Holdings Summary
          </Typography>
          <Box display="flex" gap={4} flexWrap="wrap">
            <Box>
              <Typography variant="caption" color="textSecondary">
                Total Quantity
              </Typography>
              <Typography variant="h6">
                {data.total_quantity}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="textSecondary">
                Total Invested
              </Typography>
              <Typography variant="h6">
                {formatCurrency(data.total_invested)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="textSecondary">
                Average Price
              </Typography>
              <Typography variant="h6">
                {formatCurrency(data.average_price)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="textSecondary">
                Total Transactions
              </Typography>
              <Typography variant="h6">
                {data.total_transactions}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Transactions by Portfolio */}
      {data.portfolios.map((portfolio: PortfolioTransactions) => (
        <Accordion key={portfolio.portfolio_id} defaultExpanded={data.portfolios.length === 1}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
              <Typography variant="subtitle1">
                {portfolio.portfolio_name}
              </Typography>
              <Box display="flex" gap={2} mr={2}>
                <Chip
                  label={`${portfolio.quantity} shares`}
                  size="small"
                  color="primary"
                />
                <Chip
                  label={formatCurrency(portfolio.invested_amount)}
                  size="small"
                  color="secondary"
                />
                <Chip
                  label={`${portfolio.transactions.length} transactions`}
                  size="small"
                />
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Type</TableCell>
                    <TableCell align="right">Quantity</TableCell>
                    <TableCell align="right">Price</TableCell>
                    <TableCell align="right">Total Value</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Source</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {portfolio.transactions.map((transaction) => (
                    <TableRow key={transaction.id}>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          {getTransactionTypeIcon(transaction.transaction_type)}
                          <Chip
                            label={transaction.transaction_type}
                            size="small"
                            color={getTransactionTypeColor(transaction.transaction_type) as any}
                          />
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        {transaction.quantity}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(transaction.price)}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(transaction.total_value)}
                      </TableCell>
                      <TableCell>
                        {formatDate(transaction.transaction_date)}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={transaction.source}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

export default StockTransactionHistory;



